// Lecteur Spotify Web Playback IN-APP — le son sort du TÉLÉPHONE (dans l'app JARVIS).
// Charge le SDK Spotify, crée un appareil Spotify Connect à l'intérieur de l'app, et
// expose son device_id. Le serveur cible ensuite cet appareil pour jouer la musique.
// Exige un compte Premium + un jeton avec le scope `streaming` (voir /api/child/music/wp_token).
const API_BASE = process.env.REACT_APP_API_BASE ?? "";

/* eslint-disable @typescript-eslint/no-explicit-any */
let player: any = null;
let deviceId: string | null = null;
let starting = false;
let readyWaiters: ((id: string | null) => void)[] = [];

declare global {
  interface Window {
    Spotify?: any;
    onSpotifyWebPlaybackSDKReady?: () => void;
  }
}

function loadSdk(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Spotify) return resolve(true);
    window.onSpotifyWebPlaybackSDKReady = () => resolve(true);
    if (document.getElementById("spotify-wp-sdk")) return; // déjà en cours de chargement
    const s = document.createElement("script");
    s.id = "spotify-wp-sdk";
    s.src = "https://sdk.scdn.co/spotify-player.js";
    s.async = true;
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function fetchWpToken(): Promise<string | null> {
  try {
    const r = await fetch(`${API_BASE}/api/child/music/wp_token`);
    const d = await r.json();
    return d && d.ok && d.token ? d.token : null;
  } catch {
    return null;
  }
}

// Crée le lecteur in-app (idempotent). À appeler une fois en mode enfant.
export async function initPhonePlayer(): Promise<void> {
  if (player || starting) return;
  starting = true;
  try {
    const ok = await loadSdk();
    if (!ok || !window.Spotify) { starting = false; return; }
    player = new window.Spotify.Player({
      name: "JARVIS (téléphone)",
      getOAuthToken: (cb: (t: string) => void) => {
        fetchWpToken().then((t) => { if (t) cb(t); });
      },
      volume: 0.6,
    });
    player.addListener("ready", ({ device_id }: any) => {
      deviceId = device_id;
      readyWaiters.forEach((r) => r(device_id));
      readyWaiters = [];
    });
    player.addListener("not_ready", () => { deviceId = null; });
    player.addListener("initialization_error", ({ message }: any) => console.warn("[wp] init:", message));
    player.addListener("authentication_error", ({ message }: any) => console.warn("[wp] auth:", message));
    player.addListener("account_error", ({ message }: any) => console.warn("[wp] account (Premium requis):", message));
    await player.connect();
  } catch (e) {
    console.warn("[wp] connect failed:", e);
    starting = false;
  }
}

export function getPhoneDeviceId(): string | null {
  return deviceId;
}

// DOIT être appelé dans un geste utilisateur (politique autoplay mobile) pour débloquer l'audio.
export async function activatePhonePlayer(): Promise<void> {
  try {
    if (player && typeof player.activateElement === "function") await player.activateElement();
  } catch { /* ignore */ }
}

// Attend que le lecteur in-app soit prêt (device_id), avec un délai max.
export function whenPhoneReady(timeoutMs = 8000): Promise<string | null> {
  if (deviceId) return Promise.resolve(deviceId);
  if (!player && !starting) initPhonePlayer();
  return new Promise((resolve) => {
    readyWaiters.push(resolve);
    setTimeout(() => resolve(deviceId), timeoutMs);
  });
}
